import clsx from 'clsx';
import { Form, Formik, FormikProps } from 'formik';
import { useRouter } from 'next/navigation';
import { ComponentProps, FC, useEffect, useMemo, useState } from 'react';
import { useTimeoutFn } from 'react-use';
import useSWR from 'swr';

import { CheckoutDescription } from 'src/checkout/components/CheckoutDescription/CheckoutDescription';
import {
  FINANCE_QUOTE_OPTION_STATUS,
  FinanceQuoteOption,
} from 'src/checkout/components/FinanceQuoteOption/FinanceQuoteOptionLegacy';
import { FinanceQuoteRepaymentFrequencySwitch } from 'src/checkout/components/FinanceQuoteRepaymentFrequencySwitch/FinanceQuoteRepaymentFrequencySwitchLegacy';
import { VisaIssuesForm } from 'src/checkout/components/VisaIssuesForm/VisaIssuesFormLegacy';
import { CheckoutConfirmPopup } from 'src/components/Checkout/ChangePeyment/CheckoutConfirmPopup';
import { POLLING_TIMEOUT_MILLISECONDS } from 'src/constants/constants';
import { getQuotes, requestYOUxQuote } from 'src/data/FinanceApi/FinanceApi';
import { FINANCE_QUOTE_PROVIDER, FinanceQuote } from 'src/data/FinanceApi/FinanceApi.types';
import { MICROCOPY } from 'src/data/microcopy/microcopyDictionary';
import { Icon } from 'src/general/Icons/Icon';
import { Button } from 'src/general/components/Button/Button';
import { CircularProgress } from 'src/general/components/CircularProgress/CircularProgress';
import { HelperModal } from 'src/general/components/HelperModal/HelperModal';
import { Image } from 'src/general/components/Image/Image';
import { FormikOption } from 'src/general/components/forms/FormikInput/FormikInput.types';
import { FormikOnErrorTracking } from 'src/general/components/forms/FormikOnErrorTracking/FormikOnErrorTracking';
import { InputCheckboxFormik } from 'src/general/components/forms/Input/InputCheckboxFormik';
import { Checkbox } from 'src/general/components/forms/Input/atoms/OptionFieldInput';
import { useModal } from 'src/hooks/useModal';
import { PersonalisedFinanceQuotesFormValues } from 'src/personalised-quote/templates/Forms/PersonalizedQuoteForm.type';
import { getMicrocopy, getMicrocopyBulk } from 'src/stores/microcopyStore';
import { getOr } from 'src/utils/getOr';
import { pluralise } from 'src/utils/pluralise';

import { FinanceQuoteTermSlider } from '../../../components/FinanceQuoteTermSlider/FinanceQuoteTermSliderLegacy';
import styles from './CheckoutForm.module.scss';
import {
  BUY_TYPE,
  CheckoutFormProps,
  CheckoutPayloadPartial,
  FINANCE_QUOTE_REPAYMENT_FREQUENCY,
  FinanceQuotesFormValidation,
  FinanceQuotesFormValues,
  PAYMENT_OPTIONS_TYPE,
  SEND_YOUX_STATUS,
} from './CheckoutForm.type';
import { generateQuoteOptions } from './FinanceQuotesFormLegacy.helpers';

const initialValues: FinanceQuotesFormValues = {
  quoteRequestId: '',
  quoteId: null as any,
  buyType: BUY_TYPE.PERSONAL,
  repaymentFrequency: FINANCE_QUOTE_REPAYMENT_FREQUENCY.WEEKLY,
  loanTerm: 60,
  isVisaHolder: false,
  carPrice: 0,
  deposit: 0,
  quoteInfo: {} as any,
  make: '',
};

interface Props extends CheckoutFormProps<FinanceQuotesFormValues> {
  isPaymentSummary?: boolean;
  onClickUpdateFinanceInfo?: () => void;
}

const blockContent = [
  {
    icon: <Icon name="transparent-rates" />,
    content: MICROCOPY.PERSONALIZED_QUOTE_MY_FINANCE_QUOTES_BLOCK_1_CONTENT,
  },
  {
    icon: <Icon name="car-finance-application" />,
    content: MICROCOPY.PERSONALIZED_QUOTE_MY_FINANCE_QUOTES_BLOCK_2_CONTENT,
  },
  {
    icon: <Icon name="calendar-with-tick" />,
    content: MICROCOPY.PERSONALIZED_QUOTE_MY_FINANCE_QUOTES_BLOCK_3_CONTENT,
  },
];

export const FinanceQuotesForm: FC<Props> = ({
  values,
  onSubmit,
  onBack,
  hideTitle,
  isPaymentSummary,
  onSubmitTracking,
  onClickUpdateFinanceInfo,
  setValuesPartial,
}) => {
  const [isPollingQuotes, setIsPollingQuotes] = useState<boolean>(true);
  const [isSupportedByYouX, setIsSupportedByYouX] = useState<boolean>(false);
  const [quotes, setQuotes] = useState<FinanceQuote[]>([]);
  const { data: quotesData } = useSWR(isPollingQuotes ? values.quoteRequestId : undefined, getQuotes, {
    refreshInterval: 5000,
  });

  // Mechanism to persist quote data even after polling is cancelled
  useEffect(() => {
    if (quotesData) {
      const youXData = quotesData.quotes.find((item) => item.financier === 'youx');
      if (youXData) {
        setIsPollingQuotes(false);
        setIsSupportedByYouX(true);
        setQuotes([]);
      } else {
        if (quotesData.status?.has_completed) {
          setIsPollingQuotes(false);
        }
        setQuotes(quotesData.quotes);
      }
    }
  }, [quotesData]);

  // Cancel the quotes polling after POLLING_TIMEOUT
  useTimeoutFn(() => {
    setIsPollingQuotes(false);
  }, POLLING_TIMEOUT_MILLISECONDS);

  return (
    <div className={styles.financeQuotes}>
      {!hideTitle && (
        <CheckoutDescription title={getMicrocopy(MICROCOPY.CHECKOUT_FINANCE_QUOTE_FORM_HEADER)} onBack={onBack} />
      )}
      <Formik
        initialValues={{ ...initialValues, ...values }}
        onSubmit={async (values, formikHelpers) => {
          const quoteInfo = quotes.find((q) => q.quote_id === values.quoteId)!;
          await onSubmit({ ...values, quoteInfo }, formikHelpers);
        }}
        validationSchema={FinanceQuotesFormValidation}
      >
        {(props) => (
          <FinanceQuoteContentForm
            formikProps={props}
            valuesProps={values}
            isPollingQuotes={isPollingQuotes}
            quotes={quotes}
            onSubmitTracking={onSubmitTracking}
            isPaymentSummary={isPaymentSummary}
            onClickUpdateFinanceInfo={onClickUpdateFinanceInfo}
            quoteRequestId={values.quoteRequestId}
            isSupportedByYouX={isSupportedByYouX}
            setValuesPartial={setValuesPartial}
            submitButtonAboveFooter={!isPaymentSummary}
          />
        )}
      </Formik>
    </div>
  );
};

export const enum FINANCE_QUOTE_CONTENT_TYPE {
  CHECKOUT = 'checkout',
  PERSONALISED = 'personalised',
}

interface FormProps {
  formikProps: FormikProps<FinanceQuotesFormValues> | FormikProps<PersonalisedFinanceQuotesFormValues>;
  valuesProps: Partial<FinanceQuotesFormValues> | Partial<PersonalisedFinanceQuotesFormValues>;
  isPollingQuotes: boolean;
  quotes: FinanceQuote[];
  isPaymentSummary?: boolean;
  onSubmitTracking: ComponentProps<typeof FinanceQuotesForm>['onSubmitTracking'];
  onClickUpdateFinanceInfo?: () => void;
  includeCarmaFinance?: boolean;
  submitButtonAboveFooter?: boolean;
  allowMultipleQuotes?: boolean;
  quoteRequestId?: string;
  isSupportedByYouX: boolean;
  setValuesPartial?: (
    values: CheckoutPayloadPartial,
    changePaymentMethod?: boolean,
    updateFinanceInformation?: boolean,
  ) => Promise<void>;
  formType?: FINANCE_QUOTE_CONTENT_TYPE;
}

export const FinanceQuoteContentForm: FC<FormProps> = ({
  formikProps,
  quotes,
  isPollingQuotes,
  isPaymentSummary,
  onSubmitTracking,
  onClickUpdateFinanceInfo,
  quoteRequestId,
  setValuesPartial,
  isSupportedByYouX = false,
  includeCarmaFinance = true,
  submitButtonAboveFooter = false,
  allowMultipleQuotes = false,
  formType = FINANCE_QUOTE_CONTENT_TYPE.CHECKOUT,
}) => {
  const router = useRouter();
  const [openChangePayment, setOpenChangePayment] = useState<boolean>(false);
  const [isSendSupportedByYouX, setIsSendSupportedByYouX] = useState<SEND_YOUX_STATUS>(SEND_YOUX_STATUS.UNSENT);
  const { isVisible: isVisaIssuesModalVisible, open: openVisaIssuesModal } = useModal();
  const isAvailable = quotes?.some((item) => item.state === FINANCE_QUOTE_OPTION_STATUS.AVAILABLE);
  const isDisplayButtonAbove = submitButtonAboveFooter || isSupportedByYouX || quotes.length === 0;
  const {
    visaIssuesLabel,
    visaIssuesContent,
    buttonChangeLabel,
    buttonRequestCallbackLabel,
    buttonContinueWithFinance,
    buttonBackToHome,
    providerCarmaLabel,
    financeInfoButtonLabel,
    quoteNote,
    quoteNotePaymentSummary,
    quoteLoadingLabel,
    quoteSummaryButtonLabel,
    importantInformation,
  } = getMicrocopyBulk({
    visaIssuesLabel: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_ISSUES_TITLE],
    visaIssuesContent: allowMultipleQuotes
      ? [MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_ISSUES_PERSONALIZED_DESCRIPTION]
      : [MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_ISSUES_CHECKOUT_DESCRIPTION],
    buttonChangeLabel: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_ISSUES_BUTTON_CHANGE_LABEL],
    buttonRequestCallbackLabel: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_ISSUES_REQUEST_CALLBACK_LABEL],
    buttonContinueWithFinance: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_ISSUES_CONTINUE_WITH_FINANCE_LABEL],
    buttonBackToHome: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_VISA_ISSUES_BACK_TO_HOME_LABEL],
    providerCarmaLabel: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_PROVIDER_CARMA, true],
    financeInfoButtonLabel: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_UPDATE_FINANCE_INFO_BUTTON_LABEL],
    quoteNote: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_NOTE, true],
    quoteNotePaymentSummary: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_NOTE_PAYMENT_SUMMARY],
    quoteSummaryButtonLabel: [MICROCOPY.CHECKOUT_PAYMENT_SUMMARY_FORM_MODAL_SUBMIT_LABEL],
    quoteLoadingLabel: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_LOADING_LABEL],
    importantInformation: [MICROCOPY.CHECKOUT_FINANCE_QUOTE_INFO_FORM_IMPORTANT_INFORMATION, true],
  });

  const quoteOptions = useMemo(
    () =>
      generateQuoteOptions(
        quotes,
        formikProps.values.loanTerm,
        formikProps.values.repaymentFrequency,
        isPollingQuotes,
        formikProps.values.buyType,
        formikProps.values.carPrice,
        formikProps.values.deposit,
      ),
    [
      quotes,
      formikProps.values.loanTerm,
      formikProps.values.repaymentFrequency,
      isPollingQuotes,
      formikProps.values.buyType,
      formikProps.values.carPrice,
      formikProps.values.deposit,
    ],
  );

  const submitText = allowMultipleQuotes
    ? getMicrocopy(MICROCOPY.PERSONALIZED_QUOTE_QUOTES_LIST_SUBMISSION_BUTTON_LABEL, false, {
        quotes: pluralise('quote', getOr(formikProps, 'values.quoteId.length', 0)),
      })
    : getMicrocopy(MICROCOPY.CHECKOUT_BYOF_CARMA_FINANCE_FORM_SUBMIT_BUTTON_LABEL);

  useEffect(() => {
    const { quoteId } = formikProps.values;
    if (quoteId) {
      const selectedQuote = quotes.find((item) => item.quote_id === quoteId);
      if (selectedQuote) {
        formikProps.setFieldValue('loanTerm', selectedQuote.offer.term_months);
      }
    }
  }, [quotes, formikProps.values.quoteId]);

  useEffect(() => {
    // If temporary resident, set term to first returned (in case of visa expiry)
    if (isAvailable && formikProps.values.isVisaHolder) {
      const quoteAvailable = quotes.find((item) => item.state === FINANCE_QUOTE_OPTION_STATUS.AVAILABLE);
      formikProps.setFieldValue('loanTerm', quoteAvailable?.offer.term_months);
    }
  }, [isAvailable, formikProps.values.isVisaHolder]);

  const options: FormikOption[] = quoteOptions
    .filter((option) => !(option.provider === FINANCE_QUOTE_PROVIDER.CARMA && !(includeCarmaFinance && isAvailable)))
    .map((option): FormikOption => {
      if (option.provider === FINANCE_QUOTE_PROVIDER.CARMA) {
        return {
          value: FINANCE_QUOTE_PROVIDER.CARMA,
          disabled: formikProps.isSubmitting,
          label: <div className="multilines">{providerCarmaLabel}</div>,
        };
      }
      return {
        value: option.value,
        disabled: formikProps.isSubmitting || option.status !== FINANCE_QUOTE_OPTION_STATUS.AVAILABLE,
        label: (
          <FinanceQuoteOption
            {...option}
            carPrice={formikProps.values.carPrice}
            deposit={formikProps.values.deposit}
            isSupportedByYouX={isSupportedByYouX}
          />
        ),
      };
    });

  const label = (
    <>
      <div className={styles.financeOptionLabel}>
        <FinanceQuoteTermSlider fieldName="loanTerm" quotes={quotes} stepInterval={6} />
        <FinanceQuoteRepaymentFrequencySwitch fieldName="repaymentFrequency" />
      </div>
      {isPollingQuotes && (
        <div className={styles.financeQuoteLoading}>
          <CircularProgress /> <p>{quoteLoadingLabel}</p>
        </div>
      )}
    </>
  );

  const sendContactYouX = async () => {
    setIsSendSupportedByYouX(SEND_YOUX_STATUS.LOADING);
    await requestYOUxQuote(quoteRequestId ?? '');
    setIsSendSupportedByYouX(SEND_YOUX_STATUS.SENT);
    openVisaIssuesModal();
  };

  const changePaymentMethod = async () => {
    await setValuesPartial!({});
  };

  const backToHome = () => {
    router.push('/');
  };

  const continueWithFinance = async () => {
    await formikProps.setFieldValue('quoteId', FINANCE_QUOTE_PROVIDER.CARMA);
    requestYOUxQuote(quoteRequestId ?? '');
    formikProps.handleSubmit();
  };

  const onCloseChangePayment = () => {
    setOpenChangePayment(false);
  };

  const onChange = () => {
    setValuesPartial?.({ paymentOption: { paymentOption: PAYMENT_OPTIONS_TYPE.MY_OWN_FUND } });
  };

  return (
    <>
      <Form className={styles.form} data-testid="finance-quote-content-form">
        {!!onSubmitTracking && <FormikOnErrorTracking onError={onSubmitTracking} />}
        {allowMultipleQuotes ? (
          <InputCheckboxFormik
            id="quoteId"
            name="quoteId"
            variant="group"
            inputIcon={Checkbox}
            label={label}
            className={clsx(styles.disabledOption, styles.radioContainer, {
              [styles.error]: formikProps.errors.quoteId && formikProps.touched.quoteId,
            })}
            options={options}
            data-testid="finance-quote-options"
          />
        ) : (
          <InputCheckboxFormik
            name="quoteId"
            id="quoteId"
            variant="group"
            inputIcon={Checkbox}
            label={label}
            // checkedIcon={null}
            className={clsx(styles.disabledOption, styles.radioContainer, {
              [styles.error]: formikProps.errors.quoteId && formikProps.touched.quoteId,
            })}
            options={options}
            data-testid="finance-quote-options"
          />
        )}
        {!isPaymentSummary && !allowMultipleQuotes && (
          <p className={styles.changePaymentStep7}>
            <button
              type="button"
              className={styles.licenseFinderButton}
              onClick={() => setOpenChangePayment(true)}
              data-testid="change-payment-method"
            >
              <span className={styles.egText}>{getMicrocopy(MICROCOPY.CHECKOUT_CHANGE_PAYMENT_OPTION_LABEL)}</span>
            </button>
          </p>
        )}
        {isPaymentSummary && (
          <div className={styles.financeInfoButtonContainer}>
            <Button
              type="button"
              isLoading={formikProps.isSubmitting}
              fullWidth
              data-testid="onClickUpdateFinanceInfo"
              onClick={onClickUpdateFinanceInfo}
              variant="secondary"
              className={styles.financeInfoButton}
            >
              {financeInfoButtonLabel}
            </Button>
          </div>
        )}
        <div className={clsx(styles.disclaimer)}>
          <HelperModal
            title="* Important terms and information"
            entryId="5kIh1h3Dptidf3vfQmjfiZ"
            modalTitle="Important information"
            className={styles.helperModal}
            closeButton
            data-testid="important-information"
          >
            {importantInformation}
          </HelperModal>
        </div>
        <div className={clsx(styles.bottomContent, { [styles.submitAboveFooter]: isDisplayButtonAbove })}>
          <div>
            {formType === FINANCE_QUOTE_CONTENT_TYPE.PERSONALISED && (
              <div className={styles.blockUSPContainer}>
                {blockContent.map((data) => (
                  <div key={data.content}>
                    <div className={styles.uspIcon}>{data.icon}</div>
                    <div className={styles.uspContent}>{getMicrocopy(data.content)}</div>
                  </div>
                ))}
              </div>
            )}
            <div
              className={clsx(styles.note, {
                [styles.noteFinanceInfo]: isPaymentSummary,
              })}
            >
              {isPaymentSummary ? quoteNotePaymentSummary : quoteNote}
            </div>
          </div>
          <footer
            className={clsx(styles.footer, styles.removeTopMargin, {
              [styles.financeQuoteModalFooter]: isPaymentSummary,
            })}
          >
            {isSupportedByYouX && (
              <div className={styles.visaIssuesBlock}>
                <div className={styles.visaIcon}>
                  <Image src="/images/components/FinanceQuotesForm/visa-issues.svg" width={56} height={56} alt="" />
                </div>
                <div className={styles.visaIssuesContent}>
                  <p className={styles.title}>{visaIssuesLabel}</p>
                  <p className={styles.content}>{visaIssuesContent}</p>
                </div>
                <div className={styles.groupButton}>
                  <Button
                    type="button"
                    onClick={allowMultipleQuotes ? sendContactYouX : continueWithFinance}
                    isDisabledWhileLoading
                    isLoading={formikProps.isSubmitting || isSendSupportedByYouX === SEND_YOUX_STATUS.LOADING}
                    fullWidth
                    disabled={isSendSupportedByYouX === SEND_YOUX_STATUS.SENT}
                    data-testid="FinanceQuoteContentForm-Continue-Button"
                  >
                    {allowMultipleQuotes ? buttonRequestCallbackLabel : buttonContinueWithFinance}
                  </Button>

                  {!allowMultipleQuotes && (
                    <Button
                      type="button"
                      onClick={changePaymentMethod}
                      variant="secondary"
                      isDisabledWhileLoading
                      isLoading={formikProps.isSubmitting}
                      fullWidth
                      data-testid="FinanceQuoteContentForm-ChangePayment-Button"
                    >
                      {buttonChangeLabel}
                    </Button>
                  )}
                </div>
              </div>
            )}
            {isAvailable && (
              <Button
                type="submit"
                isDisabledWhileLoading
                isLoading={formikProps.isSubmitting}
                fullWidth
                data-testid="submit"
                className={clsx(styles.financeQuotesButton, {
                  [styles.financeQuotesModalButton]: isPaymentSummary,
                })}
              >
                {isPaymentSummary ? quoteSummaryButtonLabel : submitText}
              </Button>
            )}
            {!isAvailable && !isSupportedByYouX && (
              <Button
                type="button"
                isDisabledWhileLoading
                onClick={allowMultipleQuotes ? backToHome : changePaymentMethod}
                isLoading={formikProps.isSubmitting}
                fullWidth
                data-testid="submit"
                className={clsx(styles.financeQuotesButton, {
                  [styles.financeQuotesModalButton]: isPaymentSummary,
                })}
              >
                {allowMultipleQuotes ? buttonBackToHome : buttonChangeLabel}
              </Button>
            )}
          </footer>
        </div>
        <CheckoutConfirmPopup
          microcopy={{
            title: getMicrocopy(MICROCOPY.CHECKOUT_CHANGE_PAYMENT_TITLE),
            description: getMicrocopy(MICROCOPY.CHECKOUT_CHANGE_PAYMENT_DESCRIPTION),
            backButtonLabel: getMicrocopy(MICROCOPY.CHECKOUT_CHANGE_PAYMENT_BACK_BUTTON_LABEL),
            continueButtonLabel: getMicrocopy(MICROCOPY.CHECKOUT_CHANGE_PAYMENT_CONTINUE_BUTTON_LABEL),
          }}
          isOpen={openChangePayment}
          onClose={onCloseChangePayment}
          onChange={onChange}
          data-testid="checkout-confirm-popup"
        />
      </Form>
      {isSupportedByYouX && (
        <VisaIssuesForm isOpen={isVisaIssuesModalVisible} backToHome={backToHome} data-testid="visa-issues-form" />
      )}
    </>
  );
};
